// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc, ta-code-conventions/has-matching-test */

import { inputLength } from '@ta/common.string';

import type { Fetcher, FetcherResults } from '../Typeahead';

/**
 * The type of results.
 */
export type ResultType =
  | 'geo'
  | 'nbrhd' // neighborhood
  | 'attr' // attraction
  | 'hotel'
  | 'eat' // eatery
  | 'air' // airport
  | 'flights_to'
  | 'vr'
  | 'theme_park'
  | 'tg' // travel guide
  | 'al' // airline
  | 'ship'
  | 'cruise_line'
  | 'car'
  | 'keywords'
  | 'tag'
  | 'act' // activity
  | 'uni' // university
  | 'train'
  | 'profiles';

/** This is the list of standard types to search for.  Defined here so it can be shared */
export const standardSearchTypes: Array<ResultType> = [
  'act',
  'air',
  'al',
  'ship',
  'cruise_line',
  'car',
  'attr',
  'eat',
  'geo',
  'hotel',
  'nbrhd',
];

type FilterType =
  | 'region'
  | 'nobroad'
  | 'has_eat'
  | 'has_attr'
  | 'no_closed'
  | 'is_vr'
  | 'novirtual';

/**
 * The type of links you want returned from /TypeaheadJson. For example 'hotel' will get you links to hotels in the
 * results.
 */
export type LinkType =
  | 'geo'
  | 'air'
  | 'airwd'
  | 'hotel'
  | 'ship'
  | 'cruise_line'
  | 'car'
  | 'attr'
  | 'eat'
  | 'evr'
  | 'vr'
  | 'flights_to'
  | 'nbrhd'
  | 'theme_park'
  | 'tg'
  | 'al'
  | 'act'
  | 'uni'
  | 'train'
  | 'tag'
  | 'keywords'
  | 'none';

// because we use these prop names for URL param names we are looking for an exact type match to avoid typo-bugs
type Props = {|
  /** If we should cache responses for future queries. Defaults to true */
  cache?: boolean,

  /* results are interleaved by type, rather than grouping all of the restaurants, etc together */
  interleaved?: boolean,

  /* sort can be "default", "distance" or other algorithms specified in backend */
  sort?:
    | 'default'
    | 'distance'
    | 'best_nearby'
    | 'weighted_position'
    | 'popularity'
    | 'bullseye'
    | 'distance_and_popularity',

  /** The types to include. May be one or more of: geo, neighborhood, attraction, hotel, vacation_rental, eatery,
   * airport, flights_to, flight_review, vr, theme_park, travel_guide, airline, cruise_line, ship, keyword, tag,
   * travel_forum, activity, search, near_poi, university, train_station
   */
  types?: Array<ResultType>,

  /** The filter to apply. May be one or more of: region, nobroad, has_eat, has_attr, no_closed, is_vr, novirtual */
  filter?: Array<FilterType>,

  /** Parent Ids with which to scope search */
  parentids?: Array<number>,

  /** This name is misleading, actually refers to if we should exclude metadata (true to exclude it) */
  legacy_format?: boolean,

  /** Include to the url list for each result */
  urlList?: boolean,

  /** Include rescue result */
  rescue?: boolean,

  /** The maximum number of results to return, defaults to 25 */
  max?: number,

  /**
   * The number of levels deep beyond the current result name to include. For example a value of '2' for 60745 will
   * return "Boston, Massachusetts, United States". Defaults to 0.
   */
  name_depth?: number,

  /** Should we include details about the geo. Defaults to false */
  details?: boolean,

  /**
   * An optional label to indicate the UI origin of this typeahead. This gets passed sent to
   * /TypeAheadJson?action=RECORD
   */
  uiOrigin?: string,

  /** An optional label to indicate the source of this typeahead. */
  source?: string,

  /** */
  startTime?: string,

  /** */
  searchSessionId?: string,

  /**
   * The the of links to return. For example you could request 'geo' for 'types' and then 'hotel' for 'link_type' to get
   * typeahead results for geos with a name and links to their geo hotels page. Possible values are 'geo', 'air',
   * 'airwd', 'hotel', 'attr', 'eat', 'evr', 'vr', 'flights_to', 'nbrhd', 'theme_park', 'tg', 'al', 'act', 'uni'
   * 'train', 'tag', 'keywords', 'none'.
   */
  link_type?: LinkType,

  /** The relevancy threshold, should be in range 0 - 1. For example at 0.8, it roughly returns all the results have matching score that are 80% or above the top result's matching score */
  scoreThreshold?: number,

  /**
   * Whether search user profile or not
   */
  matchUserProfiles?: boolean,

  /**
   * A list of placetype ids that the fetcher should return if it is a geo search
   */
  includedPlaceTypeIds?: Array<number>,

  scope?: number,

  beforeGeoId?: number,

  afterGeoId?: number,
|};

/** The minimum number of characters required before calling the API */
const MIN_CHARS = 3;

/** The fetcher ID */
export const ID = 'typeaheadjson';

/**
 * A typeahead fetcher for the /TypeaheadJson?action=API endpoint
 */
class TypeaheadJsonFetcher implements Fetcher {
  static defaultProps = {
    scope: 1,
    beforeGeoId: 1,
    afterGeoId: 1,
  };

  id: string = ID;
  props: Props;
  cache: { [string]: FetcherResults } | null;
  mostRecentTerm: string;
  results: FetcherResults = null;
  additionalParams: { [string]: string };

  constructor(props: Props) {
    this.props = props;
    if (typeof this.props.cache === 'undefined') {
      this.props.cache = true;
    }
    if (this.props.cache) {
      this.cache = {};
    }
  }

  setAdditionalParams(params: { [string]: string }) {
    this.additionalParams = params;
  }

  getResults(): FetcherResults {
    return this.results;
  }

  fetch(term: string, context: ?{ [string]: any }, onChange: () => mixed): void {
    this.mostRecentTerm = term;

    // note that we may count the search term length different based on int'l chars
    const termLength = inputLength(term);
    if (termLength < MIN_CHARS) {
      if (this.results !== null) {
        this.results = null;
        onChange();
      }
      return;
    }

    if (this.cache && this.cache[term]) {
      const results = this.cache[term];
      if (this.results !== results) {
        this.results = results;
        onChange();
      }
      return;
    }

    let params: { [string]: string } = {
      action: 'API',
      query: term,
    };

    // get all prop keys that are actually url params to /TypeaheadJson
    const keys = Object.keys(this.props).filter(k => k !== 'cache');
    keys.forEach(key => {
      const value = this.props[key];
      if (value) {
        if (Array.isArray(value)) {
          // Need to do some explicit casting here due to https://github.com/facebook/flow/issues/5911
          params[key] = (value: Array<ResultType> | Array<FilterType> | Array<number>).join(',');
        } else {
          params[key] = String(value);
        }
      }
    });

    if (this.additionalParams) {
      params = {
        ...params,
        ...this.additionalParams,
      };
    }

    fetch(`/TypeAheadJson?${new URLSearchParams(params).toString()}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(response => {
        if (response.ok) {
          return response;
        }
        throw Error(response.statusText);
      })
      .then(response => response.json())
      .then(results => {
        if (results && this.cache) {
          this.cache[term] = results;
        }

        // If these results came in late or out of order, simply drop them
        // We don't want to display results for a previous search term
        if (term === this.mostRecentTerm) {
          if (results) {
            this.results = results.map(result => ({
              ...result,
              query: this.mostRecentTerm,
            }));

            onChange();
          } else {
            this.results = [];
          }
        }
      })
      .catch(() => {
        this.results = [];
        onChange();
      });
  }
}

export default TypeaheadJsonFetcher;
